import { type ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { type ClipboardItem, type FileRef, api, apiUploadFileProxy } from "../lib/api.ts";
import { socketService } from "../lib/socket.ts";

interface Props {
  syncKey: string;
}

function fmtBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function delay(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export default function Dashboard({ syncKey }: Props) {
  const [scriptCount, setScriptCount] = useState<number | null>(null);
  const [clipCount, setClipCount] = useState<number | null>(null);
  const [fileCount, setFileCount] = useState<number | null>(null);
  const [clips, setClips] = useState<ClipboardItem[]>([]);
  const [files, setFiles] = useState<FileRef[]>([]);
  const [showRecentClips, setShowRecentClips] = useState(false);
  const [showRecentUploads, setShowRecentUploads] = useState(false);
  const [pushText, setPushText] = useState("");
  const [pushing, setPushing] = useState(false);
  const [deleting, setDeleting] = useState<Record<string, boolean>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string>("");
  const [downloadBusy, setDownloadBusy] = useState<Record<string, boolean>>({});
  const fileInput = useRef<HTMLInputElement>(null);

  const loadCountsAndClips = useCallback(() => {
    api.sync
      .scripts(syncKey)
      .then((r) => setScriptCount(r.items.length))
      .catch(() => {});
    api.clipboard
      .list(syncKey)
      .then((r) => {
        const sorted = [...r.items].sort((a, b) => b.createdAt - a.createdAt);
        setClipCount(sorted.length);
        setClips(sorted);
      })
      .catch(() => {});
    api.files
      .list(syncKey)
      .then((r) => {
        const sorted = [...r.files].sort((a, b) => b.createdAt - a.createdAt);
        setFileCount(sorted.length);
        setFiles(sorted);
      })
      .catch(() => {});
  }, [syncKey]);

  useEffect(() => {
    loadCountsAndClips();
  }, [loadCountsAndClips]);

  useEffect(() => {
    const offClipboard = socketService.on("clipboard:push", () => loadCountsAndClips());
    const offFileReady = socketService.on("file:ready", () => loadCountsAndClips());
    return () => {
      offClipboard();
      offFileReady();
    };
  }, [loadCountsAndClips]);

  async function pushClipboard() {
    const text = pushText.trim();
    if (!text) return;
    setPushing(true);
    await api.clipboard.push(syncKey, text).catch(() => {});
    setPushText("");
    setPushing(false);
    loadCountsAndClips();
  }

  async function deleteClip(id: string) {
    setDeleting((prev) => ({ ...prev, [id]: true }));
    await api.clipboard.delete(syncKey, id).catch(() => {});
    setDeleting((prev) => ({ ...prev, [id]: false }));
    loadCountsAndClips();
  }

  async function copyClip(itemId: string, content: string) {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(itemId);
      window.setTimeout(() => {
        setCopiedId((curr) => (curr === itemId ? null : curr));
      }, 1100);
    } catch {
      // ignore clipboard API failures in restricted browser contexts
    }
  }

  function clipPreview(content: string) {
    const normalized = content.replace(/\s+/g, " ").trim();
    return normalized.length > 120 ? `${normalized.slice(0, 120)}...` : normalized;
  }

  async function handleUpload(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadStatus("");
    try {
      const { fileId } = await apiUploadFileProxy(syncKey, file);
      setUploadStatus(`Uploaded ${file.name}`);
      loadCountsAndClips();
      // Managed storage can be briefly eventual; re-check until the uploaded file is listable.
      for (let i = 0; i < 5; i += 1) {
        const listed = await api.files.list(syncKey).catch(() => null);
        if (listed?.files.some((f) => f.id === fileId)) {
          setFileCount(listed.files.length);
          break;
        }
        await delay(500);
      }
    } catch {
      setUploadStatus("Upload failed");
    } finally {
      setUploading(false);
      if (fileInput.current) {
        fileInput.current.value = "";
      }
    }
  }

  async function downloadFile(file: FileRef) {
    setDownloadBusy((prev) => ({ ...prev, [file.id]: true }));
    try {
      const { url } = await api.files.shareUrl(syncKey, file.id);
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noreferrer";
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      setUploadStatus("Download failed");
    } finally {
      setDownloadBusy((prev) => ({ ...prev, [file.id]: false }));
    }
  }

  return (
    <div className="page">
      <h1>Dashboard</h1>
      <div className="stat-grid">
        <StatCard label="Plugins" value={scriptCount} />
        <StatCard label="Clipboard items" value={clipCount} />
        <StatCard label="Files" value={fileCount} />
      </div>

      <div className="settings-section">
        <h2>Push Clipboard</h2>
        <div className="push-row">
          <textarea
            rows={2}
            placeholder="Push text to all surfaces..."
            value={pushText}
            onChange={(e) => setPushText(e.target.value)}
          />
          <button type="button" onClick={pushClipboard} disabled={pushing || !pushText.trim()}>
            {pushing ? "Pushing..." : "Push"}
          </button>
        </div>
      </div>

      <div className="settings-section">
        <div className="page-header" style={{ marginBottom: 0 }}>
          <h2 style={{ marginBottom: 0 }}>Recent Clipboard (3)</h2>
          <button type="button" onClick={() => setShowRecentClips((v) => !v)}>
            {showRecentClips ? "Hide" : "Show"}
          </button>
        </div>
        {showRecentClips && (
          <>
            {clips.length === 0 ? (
              <p className="empty">No clipboard items yet.</p>
            ) : (
              <ul className="clip-list" style={{ marginTop: 12 }}>
                {clips.slice(0, 3).map((item) => (
                  <li key={item.id} className="clip-item">
                    <pre className="clip-item__content">{clipPreview(item.content)}</pre>
                    <div className="clip-item__meta">
                      <span>{item.contentType}</span>
                      <span>{new Date(item.createdAt * 1000).toLocaleTimeString()}</span>
                      <div className="clip-item__actions">
                        <button type="button" onClick={() => copyClip(item.id, item.content)}>
                          {copiedId === item.id ? "Copied" : "Copy"}
                        </button>
                        <button
                          type="button"
                          className="clip-icon-btn"
                          onClick={() => deleteClip(item.id)}
                          disabled={Boolean(deleting[item.id])}
                          aria-label="Delete clipboard item"
                          title="Delete"
                        >
                          {deleting[item.id] ? "..." : "🗑"}
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      <div className="settings-section">
        <div className="page-header" style={{ marginBottom: 0 }}>
          <h2 style={{ marginBottom: 0 }}>Recent Uploads (3)</h2>
          <button type="button" onClick={() => setShowRecentUploads((v) => !v)}>
            {showRecentUploads ? "Hide" : "Show"}
          </button>
        </div>
        {showRecentUploads && (
          <>
            <p style={{ marginTop: 12 }}>Upload a file from dashboard using the same Wasabi-backed flow as Files.</p>
            <input ref={fileInput} type="file" onChange={handleUpload} style={{ display: "none" }} />
            <div className="file-actions">
              <button type="button" onClick={() => fileInput.current?.click()} disabled={uploading}>
                {uploading ? "Uploading..." : "Choose file and upload"}
              </button>
              {uploadStatus && (
                <span className={uploadStatus.includes("failed") ? "error-text" : "success-text"}>{uploadStatus}</span>
              )}
            </div>
            {files.length === 0 ? (
              <p className="empty">No uploads yet.</p>
            ) : (
              <ul className="clip-list" style={{ marginTop: 12 }}>
                {files.slice(0, 3).map((file) => (
                  <li key={file.id} className="clip-item">
                    <pre className="clip-item__content">{file.name}</pre>
                    <div className="clip-item__meta">
                      <span>{file.mime}</span>
                      <span>{fmtBytes(file.size)}</span>
                      <span>{new Date(file.createdAt * 1000).toLocaleTimeString()}</span>
                      <div className="clip-item__actions">
                        <button
                          type="button"
                          onClick={() => downloadFile(file)}
                          disabled={Boolean(downloadBusy[file.id])}
                        >
                          {downloadBusy[file.id] ? "Downloading..." : "Download"}
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number | null }) {
  return (
    <div className="stat-card">
      <div className="stat-card__value">{value ?? "—"}</div>
      <div className="stat-card__label">{label}</div>
    </div>
  );
}
